import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FLIGHT_STATUS_CODE } from '@common/helpers/constants/my-booking/my_booking_service.contant';
import { BookingDateFormat, DateFormat } from '@common/interfaces';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { SupportModalBlockComponent } from '@lib/components/support-class';
import { DeviceType, MobileDeviceType, MOffice } from '@lib/interfaces';
import { DateFormatPipe, StaticMsgPipe } from '@lib/pipes';
import { AswMasterService, CommonLibService, ModalService, SystemDateService } from '@lib/services';
import { Subscription } from 'rxjs';
import { Bound, GetOrderResponseData, GetOrderResponseDataAir, Type1 } from 'src/sdk-servicing';
import { shareEmailPayloadParts } from './email-modal/email-modal-payload.state';
import { MyBookingService } from '@common/services/my-booking/my-booking.service';

@Component({
  selector: 'asw-shares',
  templateUrl: './shares.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharesComponent extends SupportModalBlockComponent {
  public bounds?: Bound[];
  public startDate?: Date;
  public endDate?: Date;
  public deviceType: DeviceType = '';
  public isCalendarSectionShow: boolean = false;
  public tripType = '' as GetOrderResponseDataAir.TripTypeEnum;
  pnr: GetOrderResponseData = {};
  flights: Array<Type1> = [];
  // 言語コード
  aswLangCode: string = '';
  private _subscriptions: Subscription = new Subscription();
  constructor(
    private _common: CommonLibService,
    private _modalService: ModalService,
    public _change: ChangeDetectorRef,
    private _staticMsg: StaticMsgPipe,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _datePipe: DatePipe,
    private _aswMasterService: AswMasterService,
    private _systemDateSvc: SystemDateService,
    private _myBookingItineraryService: MyBookingItineraryService,
    private _myBookingService: MyBookingService
  ) {
    super(_common);
  }

  reload(): void {}
  init(): void {
    this._subscriptions.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        this.pnr = servicingCommon.getOrder as GetOrderResponseData;
        this.flights =
          this.pnr?.air?.bounds?.flatMap((bound: Bound) => bound.flights ?? ([] as Type1[])) ?? ([] as Type1[]);
        this.aswLangCode = this._common.aswContextStoreService.aswContextData.lang;
        this.checkDeviceType();
        this.tripType = this.pnr?.air?.tripType as GetOrderResponseDataAir.TripTypeEnum;
        this._change.markForCheck();
      })
    );
  }
  destroy(): void {}

  // (17)「予約確認メール送信モーダル表示」ボタン押下時
  async clickEmail() {
    // 1.	予約確認メール送信モーダルを表示する。
    const part = shareEmailPayloadParts(() => this.closeShareModal());
    this._modalService.showSubModal(part);
  }

  closeShareModal() {
    this.close();
  }

  /** (16) 「閉じる(旅程共有モーダル)」ボタン押下時	*/
  clickCloseShareItinerary() {
    // 1.	予約基本情報取得APIの取得結果が存在する場合、取得結果を破棄する。
    if (this._myBookingItineraryService.reservationInformation$.getValue()) {
      this._myBookingItineraryService.reservationInformation$.next(null);
    }

    // 2.	旅程共有モーダルを閉じる。
    this.close();
  }

  /** (18) 「メッセージアプリ共有(LINE)」ボタン押下時 */
  onHandleShareLine(): void {
    /**
     * 1. プロパティ.カテゴリー="servicing"、プロパティ.キー ="url.scheme.lineApplication"を基に「イベントID(1)」で取得したプロパティリストをフィルタして、LINEアプリ起動URLを取得する
     * LINEアプリ起動URLを使用し、スマートフォン上にインストール済みの当該メッセージアプリを起動する
     */
    const url = encodeURI(this._common.aswMasterService.getMPropertyByKey('servicing', 'url.scheme.lineApplication'));
    this.shareLineSms(url);
  }

  /** (19) 「メッセージアプリ共有(SMS)」ボタン押下時 */
  onHandleShareSms(): void {
    // 1.	以下の条件でASWDB(マスタ)からSMSアプリ起動URLを取得する
    // 1.1.	ユーザ共通情報.data.aswContext.mobileDeviceType="Android"の場合、
    let url = '';
    const operation = this._common.aswContextStoreService?.aswContextData?.mobileDeviceType;

    if (operation === MobileDeviceType.ANDROID) {
      // プロパティ.カテゴリ="servicing"かつプロパティ.キー = "url.smsScheme.android"
      url = this._common.aswMasterService.getMPropertyByKey('servicing', 'url.smsScheme.android');
    } else if (
      operation === MobileDeviceType.IPHONE ||
      operation === MobileDeviceType.IPAD ||
      operation === MobileDeviceType.IPOD
    ) {
      // 1.2.	ユーザ共通情報.data.aswContext.mobileDeviceType="iPhone"、または"iPad"、または"Ipod"の場合、
      // プロパティ.カテゴリ="servicing"かつプロパティ.キー = "url.smsScheme.ios"
      url = this._common.aswMasterService.getMPropertyByKey('servicing', 'url.smsScheme.ios');
    }

    if (url) {
      this.shareLineSms(url);
    }
  }

  shareLineSms(url: string = ''): void {
    // 1.	スマートフォン上にインストール済みの当該メッセージアプリを起動する。
    // 2.	友達やグループを選択し、「転送」を押すと、PNR情報より、以下項目を送信内容として選択したチャットに内容が送信される。
    // 2.1.	以下処理にてデータ連携用セグメントリストを作成する。
    const flightList: Type1[] = [];
    this.flights.forEach((flight) => {
      if (!flight?.isOpenSegment && flight?.statusCode !== FLIGHT_STATUS_CODE.UN) {
        flightList.push(flight);
      }
    });

    // 2.2 プロパティ.カテゴリー="reservationconfirm"、プロパティ.キー ="url.reservationConfirm"を基に「イベントID(1)」で取得したプロパティリストをフィルタして、予約検索画面(S01-P010)URLを取得する。
    const reservationSearchScreenURL =
      this._aswMasterService.getMPropertyByKey('reservationconfirm', 'url.reservationConfirm') ?? '';

    // 2.3.	サイトのローカル日付(MMddHHmm)を取得する。(以下、システム日付)
    const currentDateSystem = this._systemDateSvc.getFormattedSystemDate(DateFormat.MMddHHmm);

    /** [ここから、SMS/LINE連携用パラメータ作成処理] */
    // 2.4.  以下内容にて、SMS/LINE連携用パラメータを作成する。
    let smsLineText = '';
    // 件名
    const displayContent = this._myBookingItineraryService.masterData$.value.mListDataPD113?.[0]?.display_content ?? '';
    smsLineText += `${this._staticMsg.transform(
      'label.anaInternationalReservation'
    )}${currentDateSystem} ${displayContent})\n`;
    smsLineText += `${this._staticMsg.transform('label.recLoc')}: ${this.pnr?.orderId}\n`;

    // 本文
    smsLineText += `${this._staticMsg.transform('label.title.flight')}\n`;

    /** <以下、データ連携用セグメントリストの要素数繰り返し> (それぞれの要素が当該データ連携用セグメント)> */
    flightList.forEach((flight: Type1, index) => {
      // No
      // “[”＋ループ回数(1～)＋“]”
      smsLineText += `[${index + 1}]`;
      // 出発日(MMdd)
      // “：”(半角スペース) ＋ 当該データ連携用セグメント.出発日(※1)(MMdd)  (※1)出発日は以下を基に、記載のフォーマットへ変換したものを使用する。 当該データ連携用セグメント.departure.estimatedDateTimeが存在する場合、当該データ連携用セグメント.departure.estimatedDateTime 上記以外の場合、当該データ連携用セグメント.departure.dateTime
      smsLineText += `: ${
        flight?.departure?.estimatedDateTime
          ? this._datePipe.transform(flight?.departure?.estimatedDateTime, DateFormat.MMdd)
          : this._datePipe.transform(flight?.departure?.dateTime, DateFormat.MMdd)
      }`;
      // 便名キャリア
      // “ ”(半角スペース)＋当該データ連携用セグメント.marketingAirlineCode
      smsLineText += ` ${flight?.marketingAirlineCode}`;
      // 便番号
      // 当該データ連携用セグメント.marketingFlightNumber
      smsLineText += `${flight?.marketingFlightNumber}`;
      // 出発地
      // “ ”(半角スペース) + 空港名称(※1)  (※1)当該データ連携用セグメント.departure.locationCodeを基に「イベントID(1)」で取得した空港リストをフィルタして、空港名称を取得する。 空港名が取得できなかった場合は、当該データ連携用セグメント.departure.locationName
      smsLineText += ` ${this._myBookingItineraryService.getAirportLocationName(
        flight?.departure?.locationCode,
        flight?.departure?.locationName
      )}`;
      // 到着地
      // “ ”(半角スペース) + 空港名称(※1)  (※1)当該データ連携用セグメント.arrival.locationCodeを基に「イベントID(1)」で取得した空港リストをフィルタして、空港名称を取得する。  空港名が取得できなかった場合は、当該データ連携用セグメント.arrival.locationName
      smsLineText += ` ${this._myBookingItineraryService.getAirportLocationName(
        flight?.arrival?.locationCode,
        flight?.arrival?.locationName
      )}`;
      // 出発時刻(HH:mm)
      smsLineText += ` (${
        // “ ”(半角スペース)+"("+当該データ連携用セグメント.departure.出発時刻(※1)(HH:mm)+")"+" "(半角スペース)+From-To連結文字を表す文言  (※1)出発時刻は以下を基に、記載のフォーマットへ変換したものを使用する。 当該データ連携用セグメント.departure.estimatedDateTimeが存在する場合、当該データ連携用セグメント.departure.estimatedDateTime 上記以外の場合、当該データ連携用セグメント.departure.dateTime
        flight?.departure?.estimatedDateTime
          ? this._datePipe.transform(flight?.departure?.estimatedDateTime, DateFormat.HHmm)
          : this._datePipe.transform(flight?.departure?.dateTime, DateFormat.HHmm)
      }) ${this._staticMsg.transform('label.rightArrow')}`;
      // 到着時刻(HH:mm)

      smsLineText += ` (${this.getDateDiffernce(flight?.arrivalDaysDifferenceByFlight)} ${
        flight?.arrival?.estimatedDateTime
          ? this._datePipe.transform(flight?.arrival?.estimatedDateTime, DateFormat.HHmm)
          : this._datePipe.transform(flight?.arrival?.dateTime, DateFormat.HHmm)
      })\n`;
    });
    /** <ここまで、データ連携用セグメントリストの要素数繰り返し> */

    // フッター
    /** (※2)CONNECTION_KIND：「イベントID(1)」で取得したオフィス情報からCONNECTION_KINDを取得する。*/
    const CONNECTION_KIND = this._myBookingService.getConnectionKind();
    /** (※1)予約検索画面(S01-P010)URL＋"?orderId=" +PNR情報取得API応答.data.orderId+“&CONNECTION_KIND=”＋CONNECTION_KIND(※2) ＋"&"+“LANG=”＋ユーザ共通情報.data.aswContext.lang */
    const reservationSearchPageS01P010Url = `${reservationSearchScreenURL}?orderId=${this.pnr?.orderId}&CONNECTION_KIND=${CONNECTION_KIND}&LANG=${this._common.aswContextStoreService.aswContextData.lang}`;
    /** フッタータイトルを表す旨の文言＋改行“・”＋予約検索画面(S01-P010)URL(※1) */
    smsLineText +=
      `${this._staticMsg.transform('label.title.confirmReservation')}\n` + '・' + reservationSearchPageS01P010Url;

    /** [ここまで、SMS/LINE連携用パラメータ作成処理] */

    smsLineText = encodeURIComponent(smsLineText);
    const smsLineURL = `${url}${smsLineText}`;
    window.open(smsLineURL, '_self');
  }

  /** (20) 「Googleボタン」ボタン押下時 */
  clickGoogleCalendar(): void {
    try {
      const parameters: { [key: string]: string } = {
        action: 'TEMPLATE',
      };
      const bounds: Bound[] = this.pnr?.air?.bounds ?? ([] as Bound[]);
      const firstBound = bounds[0];
      const lastBound = bounds[bounds.length - 1];
      // 1.	サイトのローカル日付を取得する。
      // Using function of system, so 1. ignore

      // 2.	以下の条件でASWDB(マスタ)からプロパティを取得し、システム日付フォーマットとして保持する。
      const formatDateTimeSystem = this._aswMasterService.getMPropertyByKey(
        'format_' + this.aswLangCode,
        'url.googleCalendar'
      );

      // 3. 以下の内容をGoogleカレンダー(外部サイト)へ反映する。
      // 3.1.	以下の条件でASWDB(マスタ)からプロパティ.VALUEを取得する、URLとして保持する。
      let url = this._aswMasterService.getMPropertyByKey('otherApplicationSettings', 'url.googleCalendar');
      if (url && url?.indexOf('?')) {
        url = url.slice(0, url.indexOf('?'));
      }

      // 3.2.	Googleカレンダー(外部サイト)表示の処理を行う
      // summary (件名)
      let displayContents = '';
      this._myBookingItineraryService.masterData$.subscribe((result) => {
        displayContents = result.mListDataPD113?.[0].display_content ?? '';
      });

      parameters['text'] = `${this._staticMsg.transform('label.anaInternationalReservation')} ${this.getDateTimeSystem(
        formatDateTimeSystem
      )} ${displayContents})`;

      // start (予定開始日)
      const startDateTime =
        (firstBound?.originDepartureEstimatedDateTime
          ? firstBound?.originDepartureEstimatedDateTime
          : firstBound?.originDepartureDateTime) ?? '';

      // end (予定終了日)
      const endDateTime =
        (lastBound?.destinationArrivalEstimatedDateTime
          ? lastBound?.destinationArrivalEstimatedDateTime
          : lastBound?.destinationArrivalDateTime) ?? '';

      parameters['dates'] =
        this._datePipe.transform(startDateTime, BookingDateFormat.yyyyMMddTHHmmssZ) +
        '/' +
        this._datePipe.transform(endDateTime, BookingDateFormat.yyyyMMddTHHmmssZ);

      // description (本文)
      let description: string = `${this._staticMsg.transform('label.title.flight')}<br/>`;
      // HTML <br/> doesn't description in DD, but Layout have descriontion about newLine

      // "<以下、PNR情報取得API応答.data.air.bounds[]の要素数繰り返し> (それぞれの要素が当該バウンド)
      bounds.forEach((bound: Bound, indexBound) => {
        const flights: Type1[] = bound?.flights ?? ([] as Type1[]);
        const flightIndex: number = flights.length - 1;
        // <以下、当該バウンド.flights[]の要素数繰り返し> (それぞれの要素が当該セグメント情報)
        flights.forEach((flight: Type1, index) => {
          // 出発日(MM/dd)
          // 出発日より算出した曜日
          const dateTimeDepartureArrive =
            (flight?.departure?.estimatedDateTime
              ? flight?.departure?.estimatedDateTime
              : flight?.departure?.dateTime) ?? '';

          description += `${this._datePipe.transform(dateTimeDepartureArrive, BookingDateFormat.MMdd2)}`;

          description += ` (${this._datePipe.transform(dateTimeDepartureArrive, BookingDateFormat.EE)})`;

          // 便名キャリア+便番号
          description += ` ${flight?.marketingAirlineCode}${flight?.marketingFlightNumber}`;

          // 出発空港コード
          description += ` ${this._myBookingItineraryService.getAirportLocationName(
            flight?.departure?.locationCode,
            flight?.departure?.locationName
          )}${this._staticMsg.transform('label.rightArrow')}`;

          // 到着空港コード
          description += `${this._myBookingItineraryService.getAirportLocationName(
            flight?.arrival?.locationCode,
            flight?.arrival?.locationName
          )}`;

          // 出発時刻(HH:mm)
          const timeDeparture = flight?.departure?.estimatedDateTime
            ? this.getDateTime(flight?.departure?.estimatedDateTime, BookingDateFormat.HHmm2)
            : this.getDateTime(flight?.departure?.dateTime, BookingDateFormat.HHmm2);
          description += ` ${this._staticMsg.transform('label.time.departure', { 0: timeDeparture })}`;

          // 到着時刻(HH:mm)
          const timeArrive = flight?.arrival?.estimatedDateTime
            ? this.getDateTime(flight?.arrival?.estimatedDateTime, BookingDateFormat.HHmm2)
            : this.getDateTime(flight?.arrival?.dateTime, BookingDateFormat.HHmm2);
          description += ` ${this._staticMsg.transform('label.time.arrival', { 0: timeArrive })}`;

          // <ここまで、データ連携用セグメントリストの要素数繰り返し>（要素の最後に<br/>を追加する。）
          description += '<br/>';
        });
        // <ここまで、PNR情報取得API応答.data.air.bounds[]の要素数繰り返し>（要素の最後に<br/>を追加する。）
        description += '<br/>';
      });
      parameters['details'] = description;

      const params = new URLSearchParams();
      for (const key in parameters) {
        params.set(key, parameters[key]);
      }

      url = url + '?' + new URLSearchParams(params).toString();
      window.open(url, '_blank');
    } catch (err) {
      console.log('Error: ', err);
    }
  }

  /** (21) 「Apple Calendarボタン」ボタン押下時 */
  clickAppleCalendar(): void {
    this.getCalendarFile(true);
  }

  /**
   * Function: download file.ics, about calendar
   * @param flag true is AppleCalendar; false is Outlook
   */
  // 1. 当処理は他機能からの呼び出しを可能とする。(以降、連携スケジュールデータダウンロード処理とする。)
  getCalendarFile(flag: boolean): void {
    // [ここから、連携スケジュールデータダウンロード処理]

    const bounds: Bound[] = this.pnr?.air?.bounds ?? ([] as Bound[]);

    // ※呼び出し元より連携スケジュール形式を受け取り、スケジュール連携用ファイルの作成、ダウンロードを行う。
    // 2.	連携スケジュール形式=”iCal”として処理を行う。
    const scheduleFormat: string = flag ? 'iCal' : '';

    // 3. サイトのローカル日付を取得する。
    const localDate = this._systemDateSvc.getFormattedSystemDate();

    /**
     * 4. プロパティ.カテゴリー="format_"+ユーザ共通情報.data.aswContext.lang、
     * プロパティ.キー ="calendarDate.schedule"
     * を基に「イベントID(1)」で取得したプロパティリストをフィルタして、システム日付フォーマットを取得する。
     */
    const systemDateFormat = this._aswMasterService.getMPropertyByKey(
      `format_${this.aswLangCode}`,
      'calendarDate.schedule'
    );
    const systemDate1 = this._systemDateSvc.getFormattedSystemDate(BookingDateFormat.yyyyMMddHHmmssSSS);
    const systemDate2 = this._systemDateSvc.getFormattedSystemDate(BookingDateFormat.yyyyMMddTHHmmss);
    const systemDate3 = this._systemDateSvc.getFormattedSystemDate(systemDateFormat);

    // 5. 以下の内容を出力したICSファイルをダウンロードする。
    // ヘッダ情報
    const HEADER_INFORMATION = [
      // “BEGIN:VCALENDAR”+改行
      `BEGIN:VCALENDAR`,
      // “PRODID:-//All Nippon Airways Co., Ltd..//ANA Schedule//EN”+改行
      `PRODID:-//All Nippon Airways Co., Ltd..//ANA Schedule//EN`,
      // “VERSION:2.0”+改行
      `VERSION:2.0`,
      // “METHOD:PUBLISH”+改行
      `METHOD:PUBLISH`,
      // “BEGIN:VEVENT”+改行
      `BEGIN:VEVENT`,
      // “UID:”+システム日付(yyyyMMddHHmmssSSS)+“@ana.co.jp”+改行
      `UID:${systemDate1}@ana.co.jp`,
      // “DTSTAMP:”+システム日付(yyyyMMdd'T'HHmmss)+改行
      `DTSTAMP:${systemDate2}`,
    ];

    // (件名)
    // (※1)「イベントID(1)」で取得した表示内容
    const displayContent: string =
      this._myBookingItineraryService.masterData$.value.mListDataPD113?.[0]?.display_content ?? '';
    // “SUMMARY:”+ヘッダタイトルを表す旨の文言+“(”+カレンダー用フォーマットに変換したシステム日付+“ ”(半角スペース)+表示内容(※1)+“)”+改行
    const SUMMARY = `SUMMARY: ${this._staticMsg.transform(
      'label.anaInternationalReservation'
    )}${systemDate3} ${displayContent})`;

    // (予定開始日) - 1) 以下の条件に従って、設定する。
    const DTSTART = `DTSTART:${
      this._datePipe.transform(
        /**
         * (※1) 一便目の出発日、及び一便目の出発時間は以下を基に、GMT、及び記載のフォーマットへ変換したものを使用する。
         * 当該バウンド.originDepartureEstimatedDateTimeが存在する場合、
         * 当該バウンド.originDepartureEstimatedDateTime
         * 上記以外の場合、当該バウンド.originDepartureDateTime
         */
        bounds[0].originDepartureEstimatedDateTime || bounds[0].originDepartureDateTime || '',
        /**
         * 2) 連携スケジュール形式=”iCal”の場合、下記内容にて出力する。
         * 2.1) “DTSTART:”+ 一便目の出発日(※1)(yyyyMMdd)+“T”+ 一便目の出発時間(※1)(HHmmss)+秒(“00”)+“Z”+改行
         * 3) 上記以外の場合、下記内容にて出力する。
         * 3.1) “DTSTART:”+ 一便目の出発日(※1)(yyyyMMdd)+“T”+ 一便目の出発時間(※1)(HHmmss)+改行
         */
        scheduleFormat === 'iCal' ? BookingDateFormat.yyyyMMddTHHmmss00Z : BookingDateFormat.yyyyMMddTHHmmss
      ) ?? ''
    }`;

    // (予定終了日) - 以下の条件に従って、設定する。
    const DTEND = `DTEND:${
      this._datePipe.transform(
        /**
         * (※1) 最終便の到着日付、及び到着時間は以下を基に、GMT、及び記載のフォーマットへ変換したものを使用する。
         * 当該バウンド.destinationArrivalEstimatedDateTimeが存在する場合、
         * 当該バウンド.destinationArrivalEstimatedDateTime
         * 上記以外の場合、当該バウンド.destinationArrivalDateTime
         */
        bounds[bounds.length - 1].destinationArrivalEstimatedDateTime ||
          bounds[bounds.length - 1].destinationArrivalDateTime ||
          '',
        /**
         * 1) 連携スケジュール形式=”iCal”の場合、下記内容にて出力する。
         * 1.1) “DTEND:”+最終便の到着日付(※1)(yyyyMMdd)+“T”+最終便の到着時間(※1)(HHmmss)+秒(“00”)+“Z”+改行
         * 2) 上記以外の場合、下記内容にて出力する。
         * 2.1) “DTEND:”+最終便の到着日付(※1)(yyyyMMdd)+“T”+最終便の到着時間(※1)(HHmmss)+秒(“00”)+改行
         */
        scheduleFormat === 'iCal' ? BookingDateFormat.yyyyMMddTHHmmss00Z : BookingDateFormat.yyyyMMddTHHmmss
      ) ?? ''
    }`;

    /** (本文)
     * “DESCRIPTION:”+本文のタイトルを表す旨の文言+“(”+カレンダー用フォーマットに変換したシステム日付+“ ”(半角スペース)+表示内容(※1)+“)”+“\\n”+“\\n”+“\\n”+“\t”
     * フライトのタイトルを表す旨の文言
     * (※1)「イベントID(1)」で取得した表示内容
     */
    let DESCRIPTION = `DESCRIPTION:${this._staticMsg.transform(
      'label.anaInternationalReservation'
    )}(${systemDate3} ${displayContent})\\n\\n\\n${this._staticMsg.transform('label.title.flight')}`;
    // <以下、PNR情報取得API応答.data.air.bounds[]の要素数繰り返し> (それぞれの要素が当該バウンド)
    // <以下、当該バウンド.flights[]の要素数繰り返し> (それぞれの要素が当該セグメント情報)
    this.flights?.forEach((flight: Type1, index) => {
      const departureDateTime = flight.departure?.estimatedDateTime || flight.departure?.dateTime || '';

      // No - '“\\n”+“\t” + “[”+ループ回数(1～)+“]”
      DESCRIPTION += `\\n\t[${index + 1}] `;

      /** 出発日 (MM/dd)
       * 当該セグメント情報.departure.出発日(※1)
       * (※1)出発日は以下を基に、記載のフォーマットへ変換したものを使用する。
       * 1) 当該セグメント情報.departure.estimatedDateTimeが存在する場合、
       *    当該セグメント情報.departure.estimatedDateTime
       * 2) 上記以外の場合、当該セグメント情報.departure.dateTime
       */
      DESCRIPTION += this._datePipe.transform(departureDateTime, BookingDateFormat.MMdd2) ?? '';

      /** 出発日より算出した曜日
       * 当該セグメント情報.departure.出発日(EE)(※1)
       * (※1)出発日は以下を基に、記載のフォーマットへ変換したものを使用する。
       * 1) 当該セグメント情報.departure.estimatedDateTimeが存在する場合、
       *    当該セグメント情報.departure.estimatedDateTime
       * 2) 上記以外の場合、当該セグメント情報.departure.dateTime"
       */
      DESCRIPTION += `(${this._datePipe.transform(departureDateTime, BookingDateFormat.EE) ?? ''})`;

      // 便名キャリア+便番号
      // “ ”(半角スペース)＋当該セグメント情報.marketingAirlineCode + 当該セグメント情報.marketingFlightNumber
      DESCRIPTION += ` ${flight.marketingAirlineCode ?? ''}${flight.marketingFlightNumber ?? ''}`;

      /** 出発地
       * “ ”(半角スペース) + 空港名称(※1)
       * (※1)当該セグメント情報.departure.locationCodeを基に「イベントID(1)」で取得した空港リストをフィルタして、空港名称を取得する。
       * 空港名が取得できなかった場合は、当該セグメント情報.departure.locationName
       */
      DESCRIPTION += ` ${this._myBookingItineraryService.getAirportLocationName(
        flight.departure?.locationCode,
        flight.departure?.locationName
      )}`;

      /** 出発時刻 (HH:mm)
       * “ ”(半角スペース)+"("+当該セグメント情報.departure.出発時刻(※1)(HH:mm)+")"+" "(半角スペース)+From-To連結文字を表す文言
       * (※1)出発時刻は以下を基に、記載のフォーマットへ変換したものを使用する。
       * 当該セグメント情報.departure.estimatedDateTimeが存在する場合、
       * 当該セグメント情報.departure.estimatedDateTime
       * 上記以外の場合、当該セグメント情報.departure.dateTime
       */
      DESCRIPTION += ` (${this._datePipe.transform(
        departureDateTime,
        BookingDateFormat.HHmm2
      )}) ${this._staticMsg.transform('label.rightArrow')}`;

      /** 到着地
       * “ ”(半角スペース) + 空港名称(※1)
       * (※1)当該セグメント情報.arrival.locationCodeを基に「イベントID(1)」で取得した空港リストをフィルタして、空港名称を取得する。
       * 空港名が取得できなかった場合は、当該セグメント情報.arrival.locationName
       */
      DESCRIPTION += ` ${this._myBookingItineraryService.getAirportLocationName(
        flight?.arrival?.locationCode,
        flight?.arrival?.locationName
      )}`;

      // 到着時刻 (HH:mm)
      /** (※1)日付差は当該データ連携用セグメント.arrivalDaysDifferenceByFlightを基に以下条件にて決定する。
       * -1の場合、label.dayMinusOne(前日) +" "(半角スペース)
       * 1の場合、label.dayPlusOne(翌日)+" "(半角スペース)
       * 2の場合、label.dayPlusTwo(翌々日)+" "(半角スペース)
       */
      const daysDifference = `${this.getDateDiffernce(flight.arrivalDaysDifferenceByFlight)} `;
      /**
       * (※2)到着時刻は以下を基に、記載のフォーマットへ変換したものを使用する。
       * 当該セグメント情報.arrival.estimatedDateTimeが存在する場合、当該セグメント情報.arrival.estimatedDateTime
       * 上記以外の場合、当該セグメント情報.arrival.dateTime
       */
      const arrivalDateTime = flight.arrival?.estimatedDateTime || flight.arrival?.dateTime || '';

      // “ ”(半角スペース) + “(”+日付差(※1)+当該セグメント情報.arrival.到着時刻(※2)(HH:mm)+“)”+“\\n”+“\t” + 改行
      DESCRIPTION += ` (${daysDifference + this._datePipe.transform(arrivalDateTime, BookingDateFormat.HHmm2)})\\n\t`;
    });
    // <ここまで、当該バウンド.flights[]の要素数繰り返し>
    //<ここまで、PNR情報取得API応答.data.air.bounds[]の要素数繰り返し>

    // “END:VEVENT”+改行+“END:VCALENDAR”+改行
    const FOOTER_INFORMATION = [`END:VEVENT`, `END:VCALENDAR`];

    // 6. 出力情報を基にダウンロードファイルを作成する。
    const icsInfo = [...HEADER_INFORMATION, SUMMARY, DTSTART, DTEND, DESCRIPTION, ...FOOTER_INFORMATION].join('\n');

    // 7. ファイル名“ANASchedule_”+システム日付(yyyy_MM_dd_HH_mm)+“.ics”で新規ファイルを作成する。
    const fileName = `ANASchedule_${this._systemDateSvc.getFormattedSystemDate(
      BookingDateFormat.yyyy_MM_dd_HH_mm
    )}.ics`;

    // 8. 上記で作成した出力情報を全て文字連結し、文字コードUTF-8でファイルに出力する。
    const blob = new Blob([icsInfo], { type: 'text/calendar;charset=utf-8' });

    // 9. 作成したダウンロードファイルをダウンロードする。
    this.downloadFileIcs(fileName, blob);

    // [ここまで、連携スケジュールデータダウンロード処理]
  }

  downloadFileIcs(fileName: string, blob: Blob): void {
    const url = window.URL.createObjectURL(blob);

    // Create a link element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);

    // Append the link to the body
    document.body.appendChild(link);

    // Trigger Download
    link.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  }

  /** (22) 「Outlookボタン」ボタン押下時 */
  clickOutlook(): void {
    /**
     * 1. 連携スケジュール形式=””として処理を行う。
     * 2. Apple Calendarボタンと同様の処理を行う。
     * ※ただし、予定開始日、予定終了日について、GMTに変換しない。また、末尾の“Z”は付与しない。
     */
    this.getCalendarFile(false);
  }

  /** (1.7.5) カレンダー登録 */
  checkDeviceType() {
    this.deviceType = this._common.aswContextStoreService.aswContextData.deviceType;
    let lang = this._common.aswContextStoreService.aswContextData.lang;
    if (this.deviceType === DeviceType.PC && (lang === 'ja' || lang === 'en')) {
      this.isCalendarSectionShow = true;
    }
  }

  /** (1.7.16) ヘルプアイコン */
  getCalendarTooltipContent() {
    let msg = '';
    switch (this.tripType) {
      case GetOrderResponseDataAir.TripTypeEnum.Domestic:
        msg = this._staticMsg.transform('tooltip.domesticCalendar');
        break;
      case GetOrderResponseDataAir.TripTypeEnum.International:
        msg = this._staticMsg.transform('tooltip.internationalCalendar');
        break;
    }
    return msg;
  }

  getDateDiffernce(dayDiff: number = 0): string {
    switch (dayDiff) {
      case -1:
        return this._staticMsg.transform('label.dayMinusOne') + ' ';
      case 1:
        return this._staticMsg.transform('label.dayPlusOne') + ' ';
      case 2:
        return this._staticMsg.transform('label.dayPlusTwo') + ' ';
      default:
        return '';
    }
  }

  getDateTime(dateTime?: string, formatDate?: string): string {
    return this._datePipe.transform(dateTime, formatDate) ?? '';
  }

  getDateTimeSystem(formatDate: string): string {
    return this._systemDateSvc.getFormattedSystemDate(formatDate);
  }
}
